/**
 * Centralized configuration for workspace resource types
 * Add new resource types here - everything else is derived automatically
 */

/**
 * Resource type categories
 */
export const RESOURCE_CATEGORIES = {
    WORKSPACE_BASED: 'workspace-based', // Uses workspace-specific queries (workspace.data)
    ENTITY_BASED: 'entity-based',       // Uses workspaceEntityResources query with filterBy
};

/**
 * Master configuration for all workspace resource types
 * Add new resource types here with their category and optional config
 */
export const WORKSPACE_RESOURCE_TYPES_CONFIG = {
    class: {
        category: RESOURCE_CATEGORIES.WORKSPACE_BASED,
        title: 'Class Resources',
        type: 'class',
        entityType: 'class',
    },
    entity: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Entity Resources',
        type: 'primaryClass',
        entityType: 'entity',
    },
    team: {
        category: RESOURCE_CATEGORIES.WORKSPACE_BASED,
        title: 'Teams',
        type: 'team',
        entityType: 'team',
    },
    task: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Task Resources',
        type: 'task',
        entityType: 'system_task',
    },
    process: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Process Resources',
        type: 'process',
        entityType: 'system_process',
    },
    script: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Scripts',
        type: 'script',
        entityType: 'script',
    },
    processdefinition: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Process Definitions Resources',
        type: 'processdefinition',
        entityType: 'processdefinition',
    },
    formdefinition: {
        category: RESOURCE_CATEGORIES.ENTITY_BASED,
        title: 'Form Definitions Resources',
        type: 'formdefinition',
        entityType: 'formdefinition',
    },
};

/**
 * Derived: Get all resource type keys
 */
export const RESOURCE_TYPES = Object.keys(WORKSPACE_RESOURCE_TYPES_CONFIG);

/**
 * Derived: Get all entity-based resource types
 */
export const ENTITY_BASED_TYPES = Object.entries(WORKSPACE_RESOURCE_TYPES_CONFIG)
    .filter(([_, config]) => config.category === RESOURCE_CATEGORIES.ENTITY_BASED)
    .map(([key]) => key);

/**
 * Derived: Get all workspace-based resource types
 */
export const WORKSPACE_BASED_TYPES = Object.entries(WORKSPACE_RESOURCE_TYPES_CONFIG)
    .filter(([_, config]) => config.category === RESOURCE_CATEGORIES.WORKSPACE_BASED)
    .map(([key]) => key);

/**
 * Get configuration for a resource type
 * @param {string} resourceType - The resource type key
 * @returns {Object|null} Configuration object or null if not found
 */
export const getResourceTypeConfig = (resourceType) => {
    return WORKSPACE_RESOURCE_TYPES_CONFIG[resourceType] || null;
};

/**
 * Check if a resource type is entity-based
 * @param {string} resourceType - The resource type key
 * @returns {boolean}
 */
export const isEntityBasedType = (resourceType) => {
    const config = WORKSPACE_RESOURCE_TYPES_CONFIG[resourceType];
    return config?.category === RESOURCE_CATEGORIES.ENTITY_BASED;
};

/**
 * Check if a resource type is workspace-based
 * @param {string} resourceType - The resource type key
 * @returns {boolean}
 */
export const isWorkspaceBasedType = (resourceType) => {
    const config = WORKSPACE_RESOURCE_TYPES_CONFIG[resourceType];
    return config?.category === RESOURCE_CATEGORIES.WORKSPACE_BASED;
};

/**
 * Get entity type for a resource type (handles mappings like task -> system_task)
 * @param {string} resourceType - The resource type key
 * @returns {string} Entity type
 */
export const getEntityType = (resourceType) => {
    const config = WORKSPACE_RESOURCE_TYPES_CONFIG[resourceType];
    return config?.entityType || resourceType;
};
